import { z } from 'zod';
import {
  CALCULATOR_FREQUENCIES,
  RETAILER_CHART_CALCULATORS,
  RETAILER_CHART_CALCULATOR_BREAKDOWNS,
  VENDOR_CHART_CALCULATORS,
  VENDOR_CHART_CALCULATOR_BREAKDOWNS,
} from '../calculator';
import { PERCENT_INCREASE_SPARK_METRICS } from '../spark';
import { objectIdRegex } from '../util';

const COMPARISON_PERIOD_OPTIONS = [
  'previousPeriodMatchDay',
  'previousYearMatchDay',
  'salesBenchmark',
] as const;

export type TComparisonPeriodOption = typeof COMPARISON_PERIOD_OPTIONS[number];

export type TChartBreakdownType =
  | 'total'
  | 'retailer'
  | 'location'
  | 'product'
  | 'brand'
  | 'category'
  | 'employee';

export type TChartDataMetric =
  | 'order_average'
  | 'total_sales'
  | 'total_units'
  | 'transaction_count'
  | 'units_per_transaction'
  | 'commission'
  | 'percent_of_total_sales'
  | 'percent_increase'
  | 'guest_check_average'
  | 'share_of_wallet_total_units'
  | 'share_of_wallet_total_sales'
  | 'basket_penetration';

export interface UISparkStanding {
  sparkId?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  locationName?: string;
  flexibleEmployeeId: string;
  posEmployeeProfileIds: string[];
  primaryValue: number;
  rank: number;
  secondaryValue?: number;
  transactionCount?: number;
  lastTransactionLocationId?: string;
  percentIncreaseComparison?: {
    currentTotal: number;
    previousTotal: number;
  };
  avatarUrl?: string;
  isParticipating?: boolean;
}

export interface StandingsPaginationMetadata {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// Percent Increase charts use a different endpoint
export interface GetChartPathParams {
  type: Omit<TChartDataMetric, 'percent_increase'>;
}

export interface GetSparkEmployeeStandingsChartPathParams {
  sparkId: string;
}

export const getChartSchema = z.object({
  groupId: z.string().min(1),
  dateStart: z.string().min(1),
  dateEnd: z.string().min(1),

  /**
   * Optional request source label.
   *
   * Used for observability (Prometheus labels) and platform routing
   * decisions on the API side.
   */
  page: z.string().min(1).max(64).optional(),

  brandGroupId: z.string().optional(),
  frequency: z.enum(CALCULATOR_FREQUENCIES).optional(),
  locationIds: z.union([z.array(z.string()), z.string()]).optional(),
  productIds: z.union([z.array(z.string()), z.string()]).optional(),
  productInternalKeys: z.array(z.string().optional()).optional(),
  sparkId: z.string().optional(),
  eventId: z.string().optional(),
  // Optimization knobs: request only specific fields and limit employee scope
  requestedAttributes: z.array(z.string()).optional(),
  employeeProfileIdsFilter: z.array(z.string()).optional(),
  percentIncreaseData: z
    .object({
      metric: z.enum(PERCENT_INCREASE_SPARK_METRICS),
      startDate: z.string(),
    })
    .optional(),
  comparisonPeriod: z.enum(COMPARISON_PERIOD_OPTIONS).optional(),
});

export type GetChartRequestBody = z.infer<typeof getChartSchema>;

export type GetChartResponseBody =
  | FullChartResponseBody
  | EmployeeChartResponseBody
  | RetailerChartLocationBody
  | RetailerChartEmployeeBody;

export const getVendorChartSchema = z.object({
  dateStart: z.string().min(1),
  dateEnd: z.string().min(1),
  frequency: z.enum(CALCULATOR_FREQUENCIES),
  retailerAccountIds: z.array(z.string().regex(objectIdRegex)),
  exclusionFilterParams: z
    .object({
      locationIds: z.array(z.string()).optional(),
      productInternalKeys: z.array(z.string()).optional(),
    })
    .optional(),
  legacyLocationIds: z.array(z.string().regex(objectIdRegex)).optional(),
  productIds: z.array(z.string().regex(objectIdRegex)).optional(),
});
export type GetVendorChartRequestBody = z.infer<typeof getVendorChartSchema>;

export const getVendorChartBucketsBreakdownPathParamsSchema = z.object({
  vendorAccountId: z.string().regex(objectIdRegex),
  breakdownType: z.enum(VENDOR_CHART_CALCULATOR_BREAKDOWNS),
  calcType: z.enum(VENDOR_CHART_CALCULATORS),
});
export type GetVendorChartBucketsBreakdownPathParams = z.infer<
  typeof getVendorChartBucketsBreakdownPathParamsSchema
>;

export const getVendorChartTotalsBreakdownPathParamsSchema = z.object({
  vendorAccountId: z.string().regex(objectIdRegex),
  breakdownType: z.enum(VENDOR_CHART_CALCULATOR_BREAKDOWNS),
  calcType: z.enum(VENDOR_CHART_CALCULATORS),
});
export type GetVendorChartTotalsBreakdownPathParams = z.infer<
  typeof getVendorChartTotalsBreakdownPathParamsSchema
>;

export type GetVendorChartPathParms = GetVendorChartTotalsBreakdownPathParams;

export const getVendorChartTotalPathParamsSchema = z.object({
  vendorAccountId: z.string().regex(objectIdRegex),
  calcType: z.enum(VENDOR_CHART_CALCULATORS),
});
export type GetVendorChartTotalPathParams = z.infer<typeof getVendorChartTotalPathParamsSchema>;

export const getVendorChartProductsWithSalesPathParamsSchema = z.object({
  vendorAccountId: z.string().regex(objectIdRegex),
});
export type GetVendorChartProductsWithSalesPathParams = z.infer<
  typeof getVendorChartProductsWithSalesPathParamsSchema
>;

export type VendorProductsWithSaleslResponseBody = {
  productsWithSales: string[];
};
export type VendorCalcTotalResponseBody = {
  total: number;
};
export type VendorTotalsChartResponseBody = {
  keys: string[];
  rows: {
    key: string;
    value?: number;
    previousValue?: number;
    [key: string]: number | string | undefined;
  }[];
};

export interface BucketsKeyedByLabel {
  [label: string]: {
    [bucketName: string]: number;
  };
}

export type VendorBreakdownBucketsChartResponseBody = {
  buckets: BucketsKeyedByLabel;
};

export interface FullChartResponseBody {
  locationBuckets: {
    [locationId: string]: {
      [bucketName: string]: number;
    };
  };
  locationCumulativeBuckets?: {
    [locationId: string]: {
      [bucketName: string]: number;
    };
  };
  locationTotals: {
    [locationId: string]: number;
  };
  employeeBuckets: {
    [flexibleEmployeeId: string]: {
      [bucketName: string]: number;
    };
  };
  /**
   * @description The value breaks down to be [primary, comparison]
   */
  employeeComparisonTotals?: {
    [flexibleEmployeeId: string]: [number, number];
  };
  employeeCumulativeBuckets?: {
    [flexibleEmployeeId: string]: {
      [bucketName: string]: number;
    };
  };
  employeeTotals: {
    [flexibleEmployeeId: string]: number;
  };
  employeeTransactionCounts?: {
    [flexibleEmployeeId: string]: number;
  };
  productBuckets?: {
    [productName: string]: {
      [bucketName: string]: number;
    };
  };
  commissions?: {
    [flexibleEmployeeId: string]: {
      commissionEarned: number;
      totalQuantity: number;
      totalPrice: number;
      hasMissingCommissions?: boolean;
    };
  };
  productsWithSales?: string[];
  productsWithSalesByKey?: string[];
  productsWithSalesByKeyArchive?: string[];
  total: number;
  totalCumulativeBuckets?: { [bucketName: string]: number };
  datePercentage?: number;
  brandBuckets?: {
    [brandName: string]: {
      [bucketName: string]: number;
    };
  };
  categoryBuckets?: {
    [categoryName: string]: {
      [bucketName: string]: number;
    };
  };
  // next two are for date-based filtering in active sparks on comparison
  effectiveDateStart?: string;
  effectiveDateEnd?: string;
}

export type EmployeeChartResponseBody = Pick<
  FullChartResponseBody,
  | 'total'
  | 'locationTotals'
  | 'employeeTotals'
  | 'employeeComparisonTotals'
  | 'employeeTransactionCounts'
  | 'commissions'
>;

export const getRetailerChartSchema = z.object({
  dateStart: z.string().min(1),
  dateEnd: z.string().min(1),
  frequency: z.enum(CALCULATOR_FREQUENCIES).optional(),
  legacyLocationIds: z.array(z.string().regex(objectIdRegex)).optional(),
});

export type GetRetailerChartRequestBody = z.infer<typeof getRetailerChartSchema>;

export type GetChartRequestBodyMerged = GetChartRequestBody & GetRetailerChartRequestBody;

export const getRetailerChartBucketsBreakdownPathParamsSchema = z.object({
  retailerAccountId: z.string().regex(objectIdRegex),
  breakdownType: z.enum(RETAILER_CHART_CALCULATOR_BREAKDOWNS),
  calcType: z.enum(RETAILER_CHART_CALCULATORS),
});

export const getRetailerChartTotalsBreakdownPathParamsSchema = z.object({
  retailerAccountId: z.string().regex(objectIdRegex),
  breakdownType: z.enum(RETAILER_CHART_CALCULATOR_BREAKDOWNS),
  calcType: z.enum(RETAILER_CHART_CALCULATORS),
});

export type GetRetailerChartTotalsBreakdownPathParams = z.infer<
  typeof getRetailerChartTotalsBreakdownPathParamsSchema
>;

export type GetRetailerChartPathParms = GetRetailerChartTotalsBreakdownPathParams;

export type RetailerBreakdownBucketsChartResponseBody = {
  buckets: BucketsKeyedByLabel;
  datePercentage: number;
};

export type RetailerBreakdownsTotalsChartResponseBody = {
  [newKey: string]: number;
};

export type RetailerChartLocationBody = {
  locationBuckets: BucketsKeyedByLabel;
  locationTotals: RetailerBreakdownsTotalsChartResponseBody;
  datePercentage: number;
  total: number;
  // TODO: these exist on the old chart api response, but not here - need to have as optional and "never" until we're fully transitioned
  employeeTotals?: never;
  commissions?: never;
};

export type RetailerChartEmployeeBody = {
  employeeTotals: RetailerBreakdownsTotalsChartResponseBody;
  // TODO: this exists on the old chart api response, but not here - need to have as optional and "never" until we're fully transitioned
  commissions?: never;
  total: number;
};

// ============================================================================
// Spark Chart Bundle Types
// ============================================================================

/**
 * Commission data for a single employee
 */
export interface SparkChartCommissionData {
  commissionEarned: number;
  totalQuantity: number;
  totalPrice: number;
  hasMissingCommissions?: boolean;
}

/**
 * Unified response containing all spark chart metrics from the gold table.
 * This eliminates the need for multiple API requests by bundling all metrics
 * (primary, secondary, derived) into a single response per comparison period.
 */
export interface SparkChartBundleResponse {
  // Primary metrics (always present)
  totalUnits: number;
  totalSales: number;
  totalTransactions: number;

  // Derived metrics (only when spark.metric matches)
  orderAverage?: number;
  unitsPerTransaction?: number;
  guestCheckAverage?: number;
  totalGuests?: number;

  // Secondary metrics (vendor sparks only - null for retailer sparks)
  shareOfWalletUnits?: number;
  shareOfWalletSales?: number;
  basketPenetration?: number;

  // Employee breakdowns (keyed by flexibleEmployeeId)
  employeeTotals: Record<string, number>;
  employeeSalesTotals: Record<string, number>;
  employeeTransactionCounts: Record<string, number>;
  employeeBuckets: Record<string, Record<string, number>>;
  employeeSalesBuckets: Record<string, Record<string, number>>;
  employeeTransactionBuckets?: Record<string, Record<string, number>>;

  // Employee derived metrics (only when spark.metric matches)
  employeeOrderAverages?: Record<string, number>;
  employeeUnitsPerTransaction?: Record<string, number>;
  employeeGuestCheckAverages?: Record<string, number>;

  // Location breakdowns
  locationTotals: Record<string, number>;
  locationSalesTotals: Record<string, number>;
  locationBuckets: Record<string, Record<string, number>>;
  locationSalesBuckets: Record<string, Record<string, number>>;
  locationTransactionTotals?: Record<string, number>;
  locationTransactionBuckets?: Record<string, Record<string, number>>;

  // Location derived metrics (only when spark.metric matches)
  locationOrderAverages?: Record<string, number>;
  locationUnitsPerTransaction?: Record<string, number>;
  locationGuestCheckAverages?: Record<string, number>;

  // Location derived metric buckets (location -> date -> value, only when spark.metric matches)
  locationOrderAverageBuckets?: Record<string, Record<string, number>>;
  locationUnitsPerTransactionBuckets?: Record<string, Record<string, number>>;
  locationGuestCheckAverageBuckets?: Record<string, Record<string, number>>;

  // Employee derived metric buckets (employee -> date -> value, only when spark.metric matches)
  employeeOrderAverageBuckets?: Record<string, Record<string, number>>;
  employeeUnitsPerTransactionBuckets?: Record<string, Record<string, number>>;
  employeeGuestCheckAverageBuckets?: Record<string, Record<string, number>>;

  // Secondary metrics per location (vendor sparks only)
  locationShareOfWalletUnits?: Record<string, number>;
  locationShareOfWalletSales?: Record<string, number>;
  locationBasketPenetration?: Record<string, number>;

  // Commission sparks only
  commissions?: Record<string, SparkChartCommissionData>;

  // For breakdown options (product IDs that have sales)
  productsWithSalesByKey: string[];

  // Date percentage for partial periods
  datePercentage: number;

  // Effective date range for this period (from gold table)
  // Used by UI to create correct date mappings for comparison periods
  effectiveDateStart: string;
  effectiveDateEnd: string;
}

/**
 * Period types for spark chart bundle requests
 */
export const SPARK_CHART_BUNDLE_PERIOD_TYPES = [
  'spark_period',
  'previous_period',
  'previous_year',
  'sales_benchmark',
] as const;

export type SparkChartBundlePeriodType = typeof SPARK_CHART_BUNDLE_PERIOD_TYPES[number];

/**
 * Maps TComparisonPeriodOption to SparkChartBundlePeriodType.
 * Used by both API and UI to convert user-facing comparison options to gold table period types.
 */
export const COMPARISON_PERIOD_TO_BUNDLE_PERIOD: Record<
  TComparisonPeriodOption | 'default',
  SparkChartBundlePeriodType
> = {
  default: 'spark_period',
  previousPeriodMatchDay: 'previous_period',
  previousYearMatchDay: 'previous_year',
  salesBenchmark: 'sales_benchmark',
};

/**
 * Request body for fetching spark chart bundle
 */
export interface GetSparkChartBundleRequestBody {
  periodType: SparkChartBundlePeriodType;
  /** Optional location filter - if provided, only include data for these locations */
  locationIds?: string[];
  /**
   * Scope control for market-wide spark detail views.
   * - retailer: current retailer-only data
   * - full: full market-wide data (vendor view)
   */
  marketWideMode?: MarketWideStandingsMode;
}

/**
 * Path params for spark chart bundle endpoint
 */
export interface GetSparkChartBundlePathParams {
  sparkId: string;
}

/**
 * Request body for fetching spark product buckets
 */
export interface GetSparkProductBucketsRequestBody {
  periodType: SparkChartBundlePeriodType;
  metric: TChartDataMetric;
  locationIds?: string[];
}

/**
 * Response body for spark product buckets endpoint
 */
export interface GetSparkProductBucketsResponseBody {
  productBuckets: Record<string, Record<string, number>>;
}

export const MARKET_WIDE_STANDINGS_MODES = ['retailer', 'full'] as const;
export type MarketWideStandingsMode = typeof MARKET_WIDE_STANDINGS_MODES[number];

export const MARKET_WIDE_BREAKDOWN_TYPES = ['retailer', 'location'] as const;
export type MarketWideBreakdownType = typeof MARKET_WIDE_BREAKDOWN_TYPES[number];
export const MARKET_WIDE_BREAKDOWN_METRICS = ['total_units', 'total_sales'] as const;
export type MarketWideBreakdownMetric = typeof MARKET_WIDE_BREAKDOWN_METRICS[number];

export interface GetSparkMarketWideBreakdownBucketsRequestBody {
  periodType: SparkChartBundlePeriodType;
  metric: MarketWideBreakdownMetric;
  breakdownType: MarketWideBreakdownType;
}

export interface GetSparkMarketWideBreakdownBucketsResponseBody {
  buckets: BucketsKeyedByLabel;
}

export interface GetSparkMarketWideStandingsRequestBody {
  mode?: MarketWideStandingsMode;
  offset?: number;
  limit?: number;
}

export interface GetSparkMarketWideStandingsResponseBody {
  standings: UISparkStanding[];
  pagination: StandingsPaginationMetadata;
  mode: MarketWideStandingsMode;
}
